<template>
    <div v-if="!isStandalone" class="text-center py-12">
        <!-- Message selon l'état d'installation -->
        <p class="text-xl font-bold mb-4">
            <span v-if="!isAppInstalled">
                🚫 Pour utiliser {{ appName }} de façon optimale, il est fortement recommandé d'installer l'application.
            </span>
            <span v-else>✅ L'application web est installée ! Vous pouvez l'ouvrir depuis l'écran d'accueil pour profiter d'une meilleur expérience.</span>
        </p>

        <!-- Modal “iOS Add to Home Screen” -->
        <transition name="fade">
            <div
                v-if="showIosInstall"
                class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
                <div class="bg-white rounded-lg shadow-lg p-6 max-w-xs text-center">
                    <h2 class="text-lg font-semibold mb-4">Installer l’application</h2>
                    <p class="mb-4 text-sm text-gray-700">
                        Pour installer cette application sur votre iPhone/iPad, appuyez sur
                        <span class="inline-block mx-1 px-2 py-1 bg-gray-200 rounded">
            ⬆️ Partager
          </span>
                        puis sélectionnez
                        <span class="inline-block mx-1 px-2 py-1 bg-gray-200 rounded">
            Sur l’écran d’accueil
          </span>.
                    </p>
                    <button
                        @click="showIosInstall = false"
                        class="px-4 py-2 bg-[#7b0f14] text-white rounded hover:bg-[#5c0c10] transition"
                    >
                        OK
                    </button>
                </div>
            </div>
        </transition>

        <!-- Bouton d'installation PWA -->
        <button
            v-if="!isAppInstalled"
            @click="promptInstall"
            class="bg-[#2b3d42] hover:bg-[#3e575e] text-white font-semibold px-6 py-3 rounded"
        >
            📲 Installer l'application
        </button>

        <!-- Bouton pour ouvrir l'application -->
        <div
            v-else-if="isAppInstalled"
            @click="openApp"
            class=" px-6 py-3"
        >
            🚀 Ouvrir mon application depuis l'écran d'accueil
        </div>
    </div>

    <div>
        <div class="relative rounded-md bg-[#f9f5f0] flex flex-col justify-between items-center text-gray-800 font-serif px-4 py-6">
            <!-- Fond en pseudo-couche, répété et à 20 % d’opacité -->
            <div
                class="absolute rounded-md  inset-0 bg-[url('/img/bg-note.jpg')] bg-repeat opacity-20 z-1"
            ></div>
            <!-- Titre -->
            <div class="relative text-center z-10">
                <h1 class="text-4xl md:text-5xl font-sans font-extrabold text-[#7b0f14] drop-shadow-sm mb-3">
                    {{ appName }}
                </h1>
                <p class="text-base md:text-lg font-sans italic max-w-md mx-auto">
                    Bienvenue sur votre espace <span class="font-semibold">journaliste</span>
                </p>
            </div>

            <!-- Image -->
            <div class="relative text-center w-full mt-6 z-10">
                <img
                    src="/img/gazette/logo.png"
                    alt="{{ appName }}"
                    class="object-cover rounded-md w-11/12 md:10/12 xl:w-4/12 mx-auto mb-6"/>
            </div>

            <div class="relative text-center z-10">
                <!-- download app scope -->
            </div>

            <!-- Bouton commencer -->
            <div class="mt-4 mb-10 relative text-center z-10">
                <router-link
                    to="/step/1"
                    class="inline-block cursor-pointer font-sans px-6 py-3 text-2xl bg-[#7b0f14] text-white no-underline xl:text-4xl   md:text-3xl font-medium rounded-full shadow-md hover:bg-[#5c0c10] transition duration-200"
                >
                    <PencilSquareIcon class="w-6 h-6 mr-1 inline-block" /> Commencer
                </router-link>
            </div>
            <div class="relative text-center z-10">
                <button
                    @click="showResetConfirm = true"
                    class="inline-block cursor-pointer font-sans px-3 py-1 text-[10px] bg-[#7b0f14] text-white rounded-full shadow-md hover:bg-[#5c0c10] transition duration-200"
                >
                    Oublier tout
                </button>
            </div>
        </div>
    </div>

    <!-- Modal de confirmation -->
    <transition name="fade">
        <div
            v-if="showResetConfirm"
            class="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
        >
            <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-sm p-6">
                <h3 class="text-lg font-semibold mb-4">
                    Réinitialiser la progression ?
                </h3>
                <p class="mb-6">
                    Cette opération effacera toutes vos étapes débloquées et votre score. Voulez-vous vraiment continuer ?
                </p>
                <div class="flex justify-end gap-4">
                    <button
                        @click="cancelResetConfirm"
                        class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                    >
                        Annuler
                    </button>
                    <button
                        @click="doResetConfirm"
                        class="px-4 py-2 bg-[#7b0f14] text-white rounded hover:bg-[#5c0c10] transition"
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>


<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { PencilSquareIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits(['update:footerVisible'])
const showResetConfirm = ref(false)
// flags d'état
const isStandalone = ref(false)
const isAppInstalled = ref(false)
const deferredPrompt = ref<Event | null>(null)
const showInstall = ref(false)
const router = useRouter()
const showIosInstall = ref(false)
const appName = import.meta.env.VITE_APP_NAME



onMounted(() => {
    // Détection du mode "standalone" (PWA installée)
    isStandalone.value =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone



    // Si l'app n'est pas en standalone, on écoute le prompt d'installation
    if (!isStandalone.value) {
        window.addEventListener('beforeinstallprompt', (e: Event) => {
            e.preventDefault()
            deferredPrompt.value = e
            showInstall.value = true
        })
    }

    // Bonus : vérifier via getInstalledRelatedApps() si la PWA est déjà installée
    if (navigator.getInstalledRelatedApps) {
        navigator.getInstalledRelatedApps().then((apps) => {
            if (apps.length > 0) {
                isAppInstalled.value = true
            }
        })
    }

    // Si on est déjà en standalone, on considère que c'est installé
    if (isStandalone.value) {
        isAppInstalled.value = true
    }

    // transmettre l'état vers App.vue
    emit('update:footerVisible', isAppInstalled.value || isStandalone.value || true)

    // détection iOS WebApp non installée
    const isIos = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())

    if (isIos && !isStandalone.value) {
        // on affiche la modal après un petit délai (p. ex. 1s)
        setTimeout(() => {
            showIosInstall.value = true
        }, 1000)
    }
    else{

    }
})

/** Lance le prompt d'installation PWA */
function promptInstall() {
    if (deferredPrompt.value && 'prompt' in deferredPrompt.value) {
        const dp = deferredPrompt.value as any
        dp.prompt()
        dp.userChoice.then((result: any) => {
            if (result.outcome === 'accepted') {
                isAppInstalled.value = true
            } else {
                alert('Vous devez installer l’application pour continuer.')
            }
            showInstall.value = false
            deferredPrompt.value = null
        })
    }
    else{
        showIosInstall.value = true
    }
}


// annule la réinitialisation
function cancelResetConfirm() {
    showResetConfirm.value = false
}

// exécution après confirmation
function doResetConfirm() {
    router.push('/reset')
    showResetConfirm.value = null
}

/** Ouvre l'application installée (deep link ou reload) */
function openApp() {
    // Pour une PWA sur Windows/Mac, recharger suffit souvent
    // Sinon, remplacer par votre schéma custom : window.location.href = 'myapp://open'
    window.location.reload()
}
</script>

<style scoped>
/* … vos styles éventuels … */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
